import React, { useEffect, useState } from 'react';
import { Drawer } from 'antd';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import TextArea from 'v3/Components/Form/TextArea';
import { PrimaryButton, TertiaryButton } from 'v3/Components/Shared/Buttons';
import './ReviewerProfile.CommentDrawer.scss';

type Props = {
  open: boolean;
  initialComment?: string;
  saving: boolean;
  deleting?: boolean;
  canDelete?: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => void;
  onDelete?: () => void;
};

export const CommentDrawer: React.FC<Props> = ({
  open,
  initialComment,
  saving,
  deleting = false,
  canDelete = false,
  onClose,
  onSubmit,
  onDelete,
}) => {
  const isEdit = initialComment != null;
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setComment(initialComment ?? '');
      setError(undefined);
    }
  }, [open, initialComment]);

  const handleSave = () => {
    if (!comment.trim()) {
      setError('Comment is required');
      return;
    }
    onSubmit(comment.trim());
  };

  const drawerTitle = (
    <div className="comment-drawer__header-inner">
      <span className="comment-drawer__title">{isEdit ? 'Edit Comment' : 'Add Comment'}</span>
      <button className="comment-drawer__close-btn" onClick={onClose} type="button">
        <CloseIcon />
      </button>
    </div>
  );

  return (
    <Drawer
      visible={open}
      onClose={onClose}
      title={drawerTitle}
      width={720}
      closable={false}
      destroyOnClose
      className="comment-drawer"
      footer={(
        <div className="comment-drawer-footer">
          <TertiaryButton title="Cancel" onClick={onClose} />
          <PrimaryButton
            title={isEdit ? 'Save' : 'Add'}
            iconPosition="start"
            onClick={handleSave}
            disabled={saving || deleting}
          >
            {saving && (
              <CircularProgress size={16} thickness={5} style={{ color: 'inherit' }} />
            )}
          </PrimaryButton>
        </div>
      )}
    >
      <div className="comment-drawer-form">
        <TextArea
          label="Comment"
          required
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            if (error) setError(undefined);
          }}
          error={error}
          rows={10}
        />
        {isEdit && canDelete && (
          <>
            <div className="comment-drawer-divider" />
            <button
              type="button"
              className="comment-drawer-delete-btn"
              onClick={onDelete}
              disabled={saving || deleting}
            >
              <CloseIcon />
              {deleting ? 'Deleting...' : 'Delete Comment'}
            </button>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default CommentDrawer;
